import React from "react";
import { useTranslation } from "react-i18next";
import VoiceAnswerRecorder from "./VoiceAnswerRecorder";

/**
 * Renders one question's input. Shared by the public answering page and the
 * builder's preview so the two can never drift — the sibling codebase keeps two
 * hand-maintained copies of this and they disagree.
 *
 * `value` is stored in the same shape the API and property_values expect:
 * string, number, boolean, or array of option strings.
 */
/** Greyed-out stand-in options shown in the builder before any are entered. */
function PreviewPlaceholderOptions({ kind }) {
    const { t } = useTranslation();
    return (
        <div className="space-y-3">
            {[1, 2, 3].map((n) => (
                <div key={n} className="flex items-center gap-3">
                    <input
                        type={kind}
                        disabled
                        readOnly
                        className={kind === "radio" ? "h-4 w-4" : "h-4 w-4 rounded"}
                    />
                    <span className="text-lg text-gray-400">{t("Questionnaire.Option", "Option")} {n}</span>
                </div>
            ))}
        </div>
    );
}

export default function QuestionInput({
    question,
    value,
    onChange,
    disabled = false,
    // Voice answers stream straight to the server as they record, so the
    // recorder needs the response it belongs to. Unset in the builder preview,
    // which never renders a live recorder.
    responseUuid = null,
    // Builder centre-pane styling: free-text answers render as a single underline
    // rather than a boxed field, so the editor reads like the finished page.
    preview = false,
    placeholder = "",
}) {
    const { t } = useTranslation();

    if (!question) return null;

    const inputClass =
        "w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black disabled:bg-gray-100";
    const previewClass =
        "w-full border-0 border-b border-gray-400 bg-transparent px-0 pb-1 text-lg text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none focus:ring-0";

    switch (question.type) {
        case "textarea":
            return preview ? (
                <input
                    type="text"
                    className={previewClass}
                    placeholder={placeholder}
                    disabled
                    readOnly
                />
            ) : (
                <textarea
                    rows={5}
                    className={inputClass}
                    value={value ?? ""}
                    disabled={disabled}
                    placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value)}
                />
            );

        case "text":
            return preview ? (
                <input
                    type="text"
                    className={previewClass}
                    placeholder={placeholder}
                    disabled
                    readOnly
                />
            ) : (
                <input
                    type="text"
                    className={inputClass}
                    value={value ?? ""}
                    disabled={disabled}
                    placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value)}
                />
            );

        case "voice":
            // In the builder preview the recorder is inert; on the public page it
            // holds the blob until the respondent moves to the next question.
            return preview ? (
                <div className="flex items-center gap-2 text-lg text-gray-400">
                    <span className="inline-block h-9 w-40 rounded-md bg-gray-200" />
                    <span className="text-sm">{placeholder || "Record your answer"}</span>
                </div>
            ) : (
                <VoiceAnswerRecorder
                    responseUuid={responseUuid}
                    questionUuid={question.uuid}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                />
            );

        case "date":
            return (
                <input
                    type="date"
                    className={inputClass}
                    value={value ?? ""}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                />
            );

        case "rating": {
            const min = Number(question.min_value ?? 1);
            const max = Number(question.max_value ?? 5);
            const scale = [];
            for (let i = min; i <= max; i += 1) scale.push(i);
            return (
                <div className="flex flex-wrap gap-2">
                    {scale.map((point) => (
                        <button
                            key={point}
                            type="button"
                            disabled={disabled}
                            onClick={() => onChange(point)}
                            className={`h-11 w-11 rounded-full border text-sm font-medium transition ${
                                value === point
                                    ? "border-black bg-black text-white"
                                    : "border-gray-300 bg-white text-gray-700 hover:border-gray-900"
                            }`}
                        >
                            {point}
                        </button>
                    ))}
                </div>
            );
        }

        case "yes_no":
            return (
                <div className="flex gap-3">
                    {[
                        { label: t("Generic.Yes", "Yes"), boolValue: true },
                        { label: t("Generic.No", "No"), boolValue: false },
                    ].map((option) => (
                        <button
                            key={option.label}
                            type="button"
                            disabled={disabled}
                            onClick={() => onChange(option.boolValue)}
                            className={`rounded-md border px-5 py-2 text-sm font-medium transition ${
                                value === option.boolValue
                                    ? "border-black bg-black text-white"
                                    : "border-gray-300 bg-white text-gray-700 hover:border-gray-900"
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            );

        case "multiple_choice":
            // Before any options are entered the builder still shows the shape
            // of the answer, so the question does not look empty while editing.
            if (preview && (question.options || []).length === 0) {
                return <PreviewPlaceholderOptions kind="radio" />;
            }
            return (
                <div className="space-y-2">
                    {(question.options || []).map((option) => (
                        <label
                            key={option}
                            className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 px-4 py-3 hover:border-gray-900"
                        >
                            <input
                                type="radio"
                                name={`q-${question.uuid}`}
                                checked={value === option}
                                disabled={disabled}
                                onChange={() => onChange(option)}
                                className="text-black focus:ring-black"
                            />
                            <span className="text-sm text-gray-800">{option}</span>
                        </label>
                    ))}
                </div>
            );

        case "multiple_select": {
            if (preview && (question.options || []).length === 0) {
                return <PreviewPlaceholderOptions kind="checkbox" />;
            }
            const selected = Array.isArray(value) ? value : [];
            return (
                <div className="space-y-2">
                    {(question.options || []).map((option) => (
                        <label
                            key={option}
                            className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 px-4 py-3 hover:border-gray-900"
                        >
                            <input
                                type="checkbox"
                                checked={selected.includes(option)}
                                disabled={disabled}
                                onChange={() =>
                                    onChange(
                                        selected.includes(option)
                                            ? selected.filter((item) => item !== option)
                                            : [...selected, option]
                                    )
                                }
                                className="rounded text-black focus:ring-black"
                            />
                            <span className="text-sm text-gray-800">{option}</span>
                        </label>
                    ))}
                </div>
            );
        }

        // Stimulus only — carries no answer and produces no transcript utterance.
        case "image_only":
        case "video_only":
            return null;

        default:
            return null;
    }
}
